import { Container, Divider } from "@material-ui/core";
import { Add, Cached, LibraryBooks, Remove } from "@material-ui/icons";
import { Box, Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteWithSearch,
  BalanceCard,
  CurrencyDisplay,
  CustomTable,
  ModalError,
  MonthYearRangePicker,
  Page,
  PrimaryButton,
  SearchTextInput,
  StatusChip,
} from "components";
import { ButtonGroupTop } from "components/atoms/Button";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import {
  downloadFileFromRes,
  formatPayloadDate,
  getErrors,
  setFormatPayloadDate,
} from "utils";
import ModalListComposer from "./ModalBalance";

const Balance = () => {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams.get("search");
  const paramsPage = urlParams.get("page");
  const paramsSize = urlParams.get("size");
  const paramsComposerId = urlParams.get("composer_id");
  const paramsStartDate = urlParams.get("start_date");
  const paramsEndDate = urlParams.get("end_date");
  const [hasMore, setHasMore] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
    composer_id: Number(paramsComposerId) || "",
    start_date: paramsStartDate ? setFormatPayloadDate(paramsStartDate) : null,
    end_date: paramsEndDate ? setFormatPayloadDate(paramsEndDate) : null,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [optionComposer, setOptionComposer] = useState([]);
  const [dataSummary, setDataSummary] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [filterParams, setFilterParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  const [loadingFilter, setLoadingFilter] = useState(false);

  const handleChangePageParams = (value, key) => {
    const newValue = key === "composer" ? value?.composer_id : value;
    urlParams.set(key, newValue);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };
  const handleChangeStartDate = date => {
    handleChangeQueryParams(date, "start_date");
    handleChangePageParams(formatPayloadDate(date), "start_date");
  };
  const handleChangeEndDate = date => {
    handleChangeQueryParams(date, "end_date");
    handleChangePageParams(formatPayloadDate(date), "end_date");
  };

  const handleChangeFilterParams = (value, key) => {
    setFilterParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const getOptionComposer = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/list-composer`,
        {
          headers,
          params: filterParams,
        }
      );
      const { data, meta } = res?.data;
      const newOptions = data?.map(item => ({
        ...item,
        id: item?.composer_id,
      }));
      setOptionComposer(prev => {
        const combined =
          filterParams.page === 1 ? newOptions : [...prev, ...newOptions];
        setHasMore(meta?.total > combined.length);
        return combined;
      });
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingFilter(false);
    }
  };

  const getDataTable = async () => {
    const { start_date, end_date, composer, ...restQueryParams } = queryParams;
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/balance`,
        {
          headers,
          params: {
            ...restQueryParams,
            start_date: start_date && formatPayloadDate(start_date),
            end_date: end_date && formatPayloadDate(end_date),
            composer_id: composer?.composer_id,
          },
        }
      );
      const { data, meta } = res?.data;
      setDataTable(data?.balances || []);
      setDataSummary({
        total_balance_composer: data?.total_balance_composer || 0,
        total_balance_publisher: data?.total_balance_publisher || 0,
      });
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  const debounceOptionComposer = useCallback(
    debounce(() => {
      getOptionComposer();
    }, 500),
    [filterParams, queryParams?.year]
  );

  const debounceDataTable = useCallback(
    debounce(async () => {
      setLoadingPage(true);
      await getDataTable();
      setLoadingPage(false);
    }, 500),
    [queryParams]
  );
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getDataTable()];
    if (isFirstLoad.current) promises.push(getOptionComposer());
    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };

  const getFileExcel = async () => {
    try {
      const {
        start_date,
        end_date,
        composer,
        ...restQueryParams
      } = queryParams;

      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/balance/export`,
        {
          headers,
          params: {
            ...restQueryParams,
            start_date: start_date && formatPayloadDate(start_date),
            end_date: end_date && formatPayloadDate(end_date),
            composer_id: composer?.composer_id,
          },
          responseType: "blob",
        }
      );
      downloadFileFromRes(res);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching && !isFirstLoad.current) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      fetchData();
    }
  }, [queryParams, debounceDataTable]);

  useEffect(() => {
    if (!isFirstLoad.current) {
      const isSearching = Boolean(filterParams?.search);
      if (isSearching) {
        debounceOptionComposer();
        return () => {
          debounceOptionComposer.cancel();
        };
      } else {
        getOptionComposer();
      }
    }
  }, [filterParams, debounceOptionComposer]);

  return (
    <Page className={classes.root} title="Balance">
      <Container maxWidth={false}>
        <HeaderTitle title="Balance" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid
            container
            columnSpacing={1}
            justifyContent="flex-start"
            width={"80%"}
          >
            <Grid item>
              <SearchTextInput
                placeholder="Search"
                value={queryParams?.search}
                onChange={e =>
                  handleChangeQueryParams(e?.target?.value, "search")
                }
              />
            </Grid>
            <Grid item>
              <AutoCompleteWithSearch
                placeholder="All Composer"
                label={"Composer"}
                options={optionComposer}
                value={queryParams?.composer || null}
                onChange={value => handleChangeQueryParams(value, "composer")}
                inputValue={filterParams?.search}
                onInputChange={value =>
                  handleChangeFilterParams(value, "search")
                }
                optionLabel={"composer_name"}
                setQueryParams={setFilterParams}
                hasMore={hasMore}
                loading={loadingFilter}
                width={300}
              />
            </Grid>
            <Grid item>
              <MonthYearRangePicker
                placeholder="Period"
                startDate={queryParams?.start_date}
                handleChangeStartDate={handleChangeStartDate}
                endDate={queryParams?.end_date}
                handleChangeEndDate={handleChangeEndDate}
                width="100%"
                endMaxDate={moment().add(5, "years")}
                clear
              />
            </Grid>
          </Grid>
          <Grid item>
            <PrimaryButton
              label="Download as excel"
              startIcon={<LibraryBooks />}
              color="rgb(24 100 23 / 85%)"
              onClick={getFileExcel}
              size="large"
            />
          </Grid>
        </Grid>
        <Box my="16px">
          <Grid container gap={2} justifyContent="flex-start">
            <BalanceCard
              title="Composer Balance"
              value={dataSummary.total_balance_composer}
              infoButton
              tooltipTitle="Composer Balance"
              onClick={() => setModalVisible(true)}
            />
            <BalanceCard
              title="Publisher Income"
              value={dataSummary.total_balance_publisher}
            />
          </Grid>
        </Box>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleView={item =>
            history.push(
              `/admin/${item?.transaction_name}/${item?.source_transaction_id}`
            )
          }
        />
        {modalVisible && (
          <ModalListComposer
            open={modalVisible}
            onClose={() => setModalVisible(false)}
          />
        )}
      </Container>
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/advance",
  },
  {
    label: "Balance",
    active: true,
  },
];
const columnTable = [
  {
    name: "date",
    title: "Date",
  },
  {
    name: "transaction_name",
    title: "Transaction",
    renderText: item =>
      item === "sales-invoice"
        ? "Sales Invoice"
        : item === "composer-invoice"
        ? "Composer Invoice"
        : item === "composer-advance"
        ? "Composer Advance"
        : item === "cost-allocation"
        ? "Cost Allocation"
        : item || "-",
  },
  {
    name: "source_transaction_number",
    title: "Source Transaction",
  },
  {
    name: "publisher_revenue",
    title: "Publisher Income",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} decimalScale={2} prefix="Rp" />
    ),
  },
  {
    name: "all",
    title: "Composer Income",
    headerAlign: "right",
    renderText: item => {
      const { balance_tag, value } = item;
      return (
        <StatusChip
          startIcon={
            balance_tag === "in" ? (
              <Add />
            ) : balance_tag === "out" ||
              balance_tag === "paid" ||
              balance_tag === "advance" ||
              balance_tag === "cost-allocation" ? (
              <Remove />
            ) : (
              <Cached />
            )
          }
          iconHeight={18}
          iconWidth={18}
          label={<CurrencyDisplay value={value} decimalScale={2} prefix="Rp" />}
          type={
            balance_tag === "in"
              ? "success"
              : balance_tag === "out" ||
                balance_tag === "paid" ||
                balance_tag === "advance" ||
                balance_tag === "cost-allocation"
              ? "danger"
              : balance_tag === "reserved"
              ? "warning"
              : "active"
          }
          textAlign="right"
        />
      );
    },
  },
  {
    name: "balance",
    title: "Composer Balance",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} decimalScale={2} prefix="Rp" />
    ),
  },
];
export default Balance;
